import { type KeyboardEvent, type MouseEvent, useEffect, useMemo, useState } from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { useAppSelector } from '@fd/customHooks/useAppSelector';
import type { Brand, Org } from '@flipdish/orgmanagement';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useQuery } from '@tanstack/react-query';
import { type DebouncedFunc, debounce } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';

import { openInNewTab } from '../../../../helpers/navigation.utils';
import { THIRTY_MINUTES } from '../../../../helpers/timeConstants';
import { notifyError } from '../../../../layouts/Notify/actions';
import { useBrandSwitchPath } from '../../OrgAndBrandSelector/useBrandSwitchPath';
import { getBrandsForOrg, getBrandsForOrgKey, getOrgByBrandId } from '../../organisation.services';
import { setCurrentOrg } from '../../rms.actions';

export const SWITCH_ORG_ID = 'switch-org';

type Options = {
  /** Load the brand list only while the selector is visible. */
  enabled: boolean;
  /** Called after a brand is chosen. */
  onClose: () => void;
  /** Called when "Switch organisation" is chosen. The host decides what replaces the list. */
  onSwitchOrg: () => void;
};

/**
 * Data and keyboard/selection logic of the brand selector, shared by the legacy top-bar menu
 * (`BrandSelectorMenuContent`) and the new header's organisation panel (`OrganisationPanel` and
 * `BrandCards` under `layouts/Portal/sidePanel/organisation`).
 */
export const useBrandSelector = ({ enabled, onClose, onSwitchOrg }: Options) => {
  const dispatch = useAppDispatch();
  const currentApp = useAppSelector((state) => state.currentApp);
  const currentOrg = useAppSelector((state) => state.rms.currentOrg);
  const theme = useTheme();
  const isMobile = !useMediaQuery(theme.breakpoints.up('sm'));
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const history = useHistory();
  const pathForBrand = useBrandSwitchPath();
  const params = useParams<{ appId: string }>();

  const { data: orgData, isPending: loadingOrg } = useQuery({
    queryKey: [getOrgByBrandId, params?.appId],
    queryFn: () => getOrgByBrandId(params?.appId || ''),
    retry: 1,
  });

  const orgId = orgData?.data?.data?.[0]?.orgId;

  const {
    data: brandsResponse,
    isLoading: loadingBrands,
    error: brandsForOrgError,
  } = useQuery({
    queryKey: [getBrandsForOrgKey, currentOrg?.orgId],
    queryFn: () => getBrandsForOrg(currentOrg?.orgId || ''),
    enabled: !!currentOrg?.orgId && enabled,
    // only reload the brands list when the selector is open and org has changed
    staleTime: THIRTY_MINUTES,
    retry: 1,
  });

  useEffect(() => {
    const brandsForOrgErrorMessage = brandsForOrgError?.message;
    if (brandsForOrgErrorMessage) {
      dispatch(notifyError({ message: brandsForOrgErrorMessage, translate: false }));
    }
  }, [brandsForOrgError]);

  useEffect(() => {
    const org = orgData?.data?.data?.[0];
    if (org && org.orgId !== currentOrg?.orgId) {
      dispatch(setCurrentOrg(org as Org));
    }
  }, [orgData, currentOrg]);

  const handleClose = () => {
    setSelectedIndex(-1);
    onClose();
  };

  const debouncedSetSearchTerm = useMemo<DebouncedFunc<(value: string) => void>>(
    () => debounce((value: string) => setSearchTerm(value.toLowerCase()), 300),
    []
  );

  const handleSearch = (value: string) => {
    debouncedSetSearchTerm(value);
    setSelectedIndex(-1);
  };

  const brands: Brand[] = brandsResponse?.data?.data || [];

  const filteredBrands = useMemo<Brand[]>(
    () =>
      brands.filter(
        (brand) =>
          brand?.name?.toLowerCase()?.includes(searchTerm) ||
          brand?.brandId?.toLowerCase()?.includes(searchTerm)
      ),
    [brands, searchTerm]
  );

  const handleChange = (
    // Absent when the choice comes from a control with no event, such as a radio group's `onChange`.
    event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement> | undefined,
    brand: { brandId?: string } | undefined,
    newTab = false
  ) => {
    const brandId = brand?.brandId;
    if (brandId === SWITCH_ORG_ID) {
      // The host decides what replaces the list (legacy: close the menu, open the drawer; panel: swap
      // content), so this must not call `onClose`, which would close the side panel it is hosted in.
      setSelectedIndex(-1);
      onSwitchOrg();
      return;
    }
    if (brandId && brandId !== 'account-details' && brandId !== 'sign-out') {
      const path = pathForBrand(brandId);
      if (
        newTab ||
        (event as MouseEvent | undefined)?.metaKey ||
        (event as MouseEvent | undefined)?.ctrlKey
      ) {
        openInNewTab(path);
      } else {
        history.push(path);
      }
    }
    handleClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const hasFilteredBrands = filteredBrands?.length;

    const totalItems = filteredBrands.length + 1;
    const isSwitchOrg = selectedIndex === filteredBrands.length || !filteredBrands.length;
    const isCommandEnter = event.metaKey || event.ctrlKey;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prevIndex) => {
          if (prevIndex === -1) {
            return 0;
          }
          return prevIndex < totalItems - 1 ? prevIndex + 1 : prevIndex;
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        hasFilteredBrands && setSelectedIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (isSwitchOrg) {
          handleChange(event, { brandId: SWITCH_ORG_ID });
        } else {
          const selectedBrand = filteredBrands[selectedIndex];
          if (selectedBrand) {
            handleChange(event, selectedBrand, isCommandEnter);
          }
        }
        break;
      default:
        break;
    }
  };

  const maxVisibleItems = isMobile ? 6 : 10;

  return {
    currentApp,
    currentOrg,
    orgId,
    brands,
    filteredBrands,
    loading: loadingBrands || loadingOrg,
    selectedIndex,
    maxVisibleItems,
    handleSearch,
    handleChange,
    handleKeyDown,
    handleClose,
  };
};

export type BrandSelectorState = ReturnType<typeof useBrandSelector>;
